import { API_URL, APP_AUTHORIZATION_TOKEN } from '@/constants/globals';
import { MINIAPP_PACKAGE_ID } from '@/miniapp/config';
import { SEARCH_PREMIUM_FORMULA, SEARCH_RESULTS_LIMIT, SEARCH_SOURCE } from '@/constants/search';
import { SearchMediaType, SearchPackageType } from '@/types/search';
import type { SearchResultItem, SearchResults } from '@/types/search';

const SHOP_ITEMS_SEARCH_URL = `${API_URL}/shop/items/search`;

/**
 * Sent by this request itself rather than left to the runtime's `fetch` patch,
 * so search doesn't wait for the runtime (which mounts only once the miniapp's
 * code has loaded). Without `x-app-authorization` the endpoint answers 401
 * `token_error`; it is the only header search needs. `x-touchpoint` matches what
 * the runtime sends. Once the runtime is up its patch adds nothing: it only sets
 * headers that are absent.
 */
const SEARCH_HEADERS = {
  'x-app-authorization': APP_AUTHORIZATION_TOKEN,
  'x-touchpoint': MINIAPP_PACKAGE_ID,
};

interface SearchApiItem {
  id: number;
  title?: string;
  url?: string;
  data_url?: string;
  width?: number;
  height?: number;
  source_info?: {
    app_payload?: {
      presetId?: string;
      genaicategory?: string;
    };
  };
}

interface SearchApiResponse {
  response?: SearchApiItem[];
  metadata?: {
    restrictionInfo?: {
      restricted_type?: string;
      message?: { text?: string };
    };
  };
}

const MEDIA_TYPE_BY_PACKAGE: Record<SearchPackageType, SearchMediaType> = {
  [SearchPackageType.Photo]: SearchMediaType.Image,
  [SearchPackageType.Video]: SearchMediaType.Video,
};

// Items without a usable source are unrenderable — drop them rather than
// letting a broken tile through.
const toSearchResultItem = (item: SearchApiItem, packageType: SearchPackageType): SearchResultItem | null => {
  if (!item.url || !item.data_url) return null;

  const payload = item.source_info?.app_payload;

  return {
    id: String(item.id),
    title: item.title?.trim() ?? '',
    // From the request, not `item.type`: photos come back as `background`.
    mediaType: MEDIA_TYPE_BY_PACKAGE[packageType],
    posterUrl: item.url,
    mediaUrl: item.data_url,
    width: item.width ?? 0,
    height: item.height ?? 0,
    presetSlug: payload?.presetId ?? null,
    categorySlug: payload?.genaicategory ?? null,
  };
};

type FetchPackageParams = {
  query: string;
  packageType: SearchPackageType;
};

const fetchPackage = async ({ query, packageType }: FetchPackageParams): Promise<SearchResults> => {
  const params = new URLSearchParams({
    premium_formula_name: SEARCH_PREMIUM_FORMULA,
    source: SEARCH_SOURCE,
    package_type: packageType,
    q: query,
    limit: String(SEARCH_RESULTS_LIMIT),
  });

  const response = await fetch(`${SHOP_ITEMS_SEARCH_URL}?${params.toString()}`, { headers: SEARCH_HEADERS });

  if (!response.ok) {
    throw new Error(`${packageType} search failed with status ${response.status}`);
  }

  const data = (await response.json()) as SearchApiResponse;
  const restriction = data.metadata?.restrictionInfo;
  const isRestricted = Boolean(restriction && restriction.restricted_type !== 'ok');

  return {
    items: (data.response ?? []).map(item => toSearchResultItem(item, packageType)).filter((item): item is SearchResultItem => item !== null),
    restrictionMessage: isRestricted ? restriction?.message?.text || null : null,
  };
};

/**
 * Search across published GenAI Studio presets — the same endpoint, params and
 * response handling as `genai-studio-v2`'s `requests/search/searchItems.ts`, so
 * a term returns the same hits on this site and inside the miniapp.
 *
 * Every hit carries the preset that produced it in
 * `source_info.app_payload.presetId`, which is what makes a result navigable
 * back to its page here.
 *
 * Photo and video are separate `package_type` queries, so both run in parallel
 * and the results concatenate (videos first — the catalogue is video-led). One
 * failing package doesn't sink the other; only a total failure throws, so a
 * caller can tell a broken search from one that matched nothing.
 *
 * A plain `fetch`, not the miniapp's SDK: the embedded runtime patches
 * `window.fetch` to add the app-authorization and user headers on `API_URL`
 * requests, so no client is needed — the same reason
 * `services/user/fetchUserAvatar.ts` needs none.
 *
 * **Browser only.** Called from a server component this returns 401
 * `token_error`: something in the server graph patches `fetch` and presents a
 * user token that isn't valid there, and this endpoint rejects a bad token even
 * though it accepts no token at all. `/search` therefore fetches from the
 * client like the header field does — it is `noIndex`, so nothing is lost by
 * not rendering its results on the server.
 */
const runSearch = async (query: string): Promise<SearchResults> => {
  const settled = await Promise.allSettled([SearchPackageType.Video, SearchPackageType.Photo].map(packageType => fetchPackage({ query, packageType })));

  const fulfilled = settled.filter((result): result is PromiseFulfilledResult<SearchResults> => result.status === 'fulfilled');

  if (!fulfilled.length) {
    throw new Error('Search failed for every package type');
  }

  return {
    items: fulfilled.flatMap(({ value }) => value.items),
    restrictionMessage: fulfilled.find(({ value }) => value.restrictionMessage)?.value.restrictionMessage ?? null,
  };
};

/**
 * In-flight and recently-settled searches, by term.
 *
 * Restores what React Query gives the miniapp and what removing it lost: one
 * term is one request. Two surfaces ask for the same term on `/search` — the
 * page's grid and the header field, which reads the term back out of `?q=` —
 * and without this they each fetch it.
 *
 * A rejection is evicted so the next ask retries rather than replaying the
 * failure; that is what makes the Retry button work.
 */
const inFlight = new Map<string, { at: number; promise: Promise<SearchResults> }>();

/** `SEARCH_STALE_TIME` in `genai-studio-v2` — results change rarely. */
const SEARCH_CACHE_MS = 5 * 60 * 1000;

const searchItems = (query: string): Promise<SearchResults> => {
  const cached = inFlight.get(query);
  if (cached && Date.now() - cached.at < SEARCH_CACHE_MS) return cached.promise;

  // No `AbortSignal` anywhere in here, deliberately: a shared request must not
  // be cancellable by one of the callers waiting on it. Callers drop results
  // they no longer want on their own (see `useSearchResults`'s request id), so
  // the only cost is that a superseded keystroke finishes in the background —
  // and the debounce in front of this makes that rare.
  const promise = runSearch(query).catch(error => {
    inFlight.delete(query);
    throw error;
  });

  inFlight.set(query, { at: Date.now(), promise });
  return promise;
};

export default searchItems;
